import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "react-jss";
import Select from "../select/Select";
import useStyles from "./TopNavigation.styles";
import { StoreState } from "../../models/states/StoreState";
import ViewResponsesButton from "./ViewResponsesButton";
import useTranslatable from "../../hooks/useTranslatable";
import { ThemeModel } from "../../models/ThemeModel";
import { changeLanguage } from "../../redux/actions";
import { CULTURE_INFO } from "../../utils/lang";
import { getIdForAriaLabelledFieldBySection } from "../../utils/WCAGHelper";
import type { Theme } from "../../types/Theme";
import { shouldShowViewResponsesAtTop } from "../../utils/viewResponsesVisibility";

interface Props {
    theme: ThemeModel;
    showSecurityQuestion: boolean;
}

export default function TopNavigation(props: Props) {
    const { theme: surveyTheme, showSecurityQuestion } = props;
    const theme = useTheme() as Theme;
    const classes = useStyles({ theme });
    const { LanguageSelectorVisible, LanguageSelectorLabelActive } = theme;
    const dispatch = useDispatch();
    const language = useSelector((state: StoreState) => state.interview.language);
    const languages = useSelector((state: StoreState) => state.interview.questionnaire.languages);
    const languageSelectorTranslatedText = useTranslatable(surveyTheme, "LanguageSelector");

    const listboxAriaLabelId = getIdForAriaLabelledFieldBySection("NAVLANGSELECTOR");

    const languageSelectorLabel = LanguageSelectorLabelActive && (
        <span id={listboxAriaLabelId}>{languageSelectorTranslatedText}</span>
    );

    const showViewResponsesAtTop = shouldShowViewResponsesAtTop(theme, showSecurityQuestion);

    return (
        <nav className={classes.topNavigation}>
            {showViewResponsesAtTop && (
                <ViewResponsesButton
                    surveyTheme={surveyTheme}
                    className="top-navigation__view-responses"
                    size="small"
                />
            )}

            {LanguageSelectorVisible && (
                <>
                    {languageSelectorLabel}
                    <Select
                        id="LANGUAGE_SELECTOR_INPUT"
                        className={classes.languageSelector}
                        value={[language]}
                        onChange={(code) => dispatch(changeLanguage(code[0]))}
                        icon="globe"
                        disabled={languages.length < 2}
                        disabledBorderOnly={languages.length < 2}
                        ariaDisabledBy={languages.length < 2}
                        options={languages.map((code) => {
                            const info = CULTURE_INFO[code.toLowerCase()];
                            const isFrench = language === "fr";
                            const label = isFrench
                                ? `${info.nativeName !== info.name_fr ? info.name_fr + " (" + info.nativeName + ")" : info.name_fr}`
                                : `${info.nativeName !== info.name ? info.name + " (" + info.nativeName + ")" : info.name}`;
                            return {
                                id: `LANGUAGE_SELECTOR_INPUT_${code.toUpperCase()}`,
                                label,
                                value: code,
                            };
                        })}
                        listboxAriaLabelId={languageSelectorLabel ? listboxAriaLabelId : null}
                    />
                </>
            )}
        </nav>
    );
}
